import { YouTubePlayer } from '@/types/youtube'
import { atom, getDefaultStore } from 'jotai'

export const hasBackgroundAudioAtom = atom(false)
const store = getDefaultStore()

type Media = HTMLMediaElement | YouTubePlayer

let currentMedia: Media | null = null

const playAudioBackgroundListeners = new Set<(detail: { src: string; time: number }) => void>()
const stopAudioBackgroundListeners = new Set<() => void>()

export function onPlayAudioBackground(listener: (detail: { src: string; time: number }) => void) {
  playAudioBackgroundListeners.add(listener)
  return () => {
    playAudioBackgroundListeners.delete(listener)
  }
}

export function onStopAudioBackground(listener: () => void) {
  stopAudioBackgroundListeners.add(listener)
  return () => {
    stopAudioBackgroundListeners.delete(listener)
  }
}

export function pause(media: Media | null) {
  if (!media) {
    return
  }
  if (isPipElement(media)) {
    return
  }
  if (currentMedia === media) {
    currentMedia = null
  }
  _pause(media)
}

export function autoPlay(media: Media) {
  if (
    document.pictureInPictureElement &&
    isMediaPlaying(document.pictureInPictureElement as HTMLMediaElement)
  ) {
    return
  }
  if (store.get(hasBackgroundAudioAtom) && currentMedia && isMediaPlaying(currentMedia)) {
    return
  }
  play(media)
}

export function play(media: Media | null) {
  if (!media) {
    return
  }
  if (document.pictureInPictureElement && document.pictureInPictureElement !== media) {
    ;(document.pictureInPictureElement as HTMLMediaElement).pause()
  }
  if (currentMedia && currentMedia !== media) {
    _pause(currentMedia)
  }
  currentMedia = media
  if (isMediaPlaying(media)) {
    return
  }

  _play(currentMedia).catch((error) => {
    console.error('Error playing media:', error)
    currentMedia = null
  })
}

export function playAudioBackground(src: string, time: number = 0) {
  for (const listener of playAudioBackgroundListeners) {
    listener({ src, time })
  }
  store.set(hasBackgroundAudioAtom, true)
}

export function stopAudioBackground() {
  for (const listener of stopAudioBackgroundListeners) {
    listener()
  }
  store.set(hasBackgroundAudioAtom, false)
}

function isYouTubePlayer(media: Media): media is YouTubePlayer {
  return (media as YouTubePlayer).pauseVideo !== undefined
}

function isMediaPlaying(media: Media) {
  if (isYouTubePlayer(media)) {
    return media.getPlayerState() === window.YT.PlayerState.PLAYING
  }
  return media.currentTime > 0 && !media.paused && !media.ended && media.readyState >= 2
}

function isPipElement(media: Media) {
  if (isYouTubePlayer(media)) {
    return false // YouTube players do not support Picture-in-Picture
  }
  if (document.pictureInPictureElement === media) {
    return true
  }
  return (media as any).webkitPresentationMode === 'picture-in-picture'
}

function _pause(media: Media) {
  if (isYouTubePlayer(media)) {
    return media.pauseVideo()
  }
  return media.pause()
}

async function _play(media: Media) {
  if (isYouTubePlayer(media)) {
    return media.playVideo()
  }
  return media.play()
}
